import { Inject, Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse
} from '@angular/common/http';
import { from, Observable, of, switchMap, tap, map } from 'rxjs';
import { IOfflineService } from '../interfaces/offline-service.interface';
import { OFFLINE_SERVICE } from '../interfaces/offline-service.token';
import { environment } from 'src/environments/environment';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { PlatformModeStateService } from './platform-mode-state.service';
import { ConnectivityService } from '@mydocma/core';
import { AuthService, JwtAuthService } from '@mydocma/auth';

@Injectable({
  providedIn: 'root'
})
export class OfflineRequestHandlerService {
  private localLayoutId = environment.appContainerConfig.capacitor.localLayoutId;

  private readonly FAULT_ENDPOINTS = [
    '/faults/search',
    '/count',
    '/extended',
    'faults?autoActivities=true',
    'faults/activities',
  ] as const;

  private excludedEndpointsForMessage = [
    'blob:',
    '/FaultFieldsMemoryValues',
    '/FieldConfig',
    '/layouts',
    '/tiles',
    '/customers',
    '/projects',
    '/bim-models',
    '/LayoutTypeSorting',
    '/assets/capacitor',
    'tasks-api/tasks',
    'connect/revocation',
    '/keywords',
    '/integrations',

  ]

  private endpointsForMessageWithCooldown = [
    '/search',
    '/count',
    '/extended',
    '/LayoutTypeSorting'
  ]

  private allowedPaths = [
    'filegroup',
    'connect/token',
    '/files-api',
    '/reporting',
    '/groups',
    'isvalid',
    '/tasks-api',
  ];

  private jwtAuthService?: JwtAuthService;

  constructor(@Inject(OFFLINE_SERVICE) private offlineService: IOfflineService,
              private platformModeStateService: PlatformModeStateService,
              private connectivityService: ConnectivityService,
              private injector: Injector) {


  }

  private getAuthService(): JwtAuthService {
    if (!this.jwtAuthService) {
      this.jwtAuthService = this.injector.get(AuthService) as any as JwtAuthService;
    }
    return this.jwtAuthService;
  }

  handleRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>>{

    //TODO: Implement Offline service for web client

    if (!this.platformModeStateService.isNative) {
      return next.handle(request);
    }

    const collection = this.getCollectionFromUrl(request.url);
    if (collection || this.allowedPaths.some(path => request.url.includes(path))) {
      return next.handle(request);
    }

    if (request.url.includes(`layouts/${this.localLayoutId}/userfields`)) {
      return this.handleUserfieldsRequest();
    }

    if (request.url.includes('assets/i18n')) {
      return this.handleI18nRequest(request);
    }

    if (request.url.includes('/material-icons')) {
      return this.handleMaterialIconsRequest(request, next);
    }

    if(this.connectivityService.isOfflineVal && this.platformModeStateService.isLocalModeEnabled && this.shouldTriggerOfflineMessage(request.url)){
      const cooldown = this.shouldTriggerOfflineMessageWithCooldown(request.url);
      this.platformModeStateService.showNoInternetMessage$.next({show: true, cooldown: cooldown, isLocalMode: this.platformModeStateService.isLocalMode});
    }

    if (this.isFaultAndAttachmentRequest(request.url)) {
      return this.handleFaultAndAttachmentRequest(request, next);
    }

    if(request.url.includes('/assets/capacitor') && request.url.includes('plans')){
      return next.handle(request);
    }

    if (request.url.includes('.well-known/openid-configuration')) {
      return this.handleCachedRequest(request, next, false);
    }
    if (request.url.includes('users/info')) {
      return this.handleCachedRequest(request, next,true, (data)=> {
        const cachedUserId = data?.user?.id;
        if (!cachedUserId) {
          return false;
        }
        const tokenUserId = this.getAuthService()?.getAccessTokenClaims()?.sub_id;
        return tokenUserId === cachedUserId;
      });
    }

    return this.handleDefaultRequest(request, next);
  }


private handleCachedRequest(request: HttpRequest<unknown>, next: HttpHandler, refreshCacheAsync = false, isCacheValidFnc?: (cachedResponse: any)=> boolean ): Observable<HttpEvent<unknown>> {

    const handleRequestAndCacheResponse = next.handle(request).pipe(
              tap(event => {
                  if (event instanceof HttpResponse && event.status === 200) {
                    this.offlineService.saveDataByUrl(request, event.body);
                  }
              }));

    const offline= this.connectivityService.isOfflineVal;
    return from(this.offlineService.getData(request)).pipe(
        switchMap(data => {
          if (offline || (data && !isCacheValidFnc) || (isCacheValidFnc && data && isCacheValidFnc(data)))  {
            if (!offline && refreshCacheAsync) {
              // if not offline, handle request and cache response without waiting
              handleRequestAndCacheResponse.subscribe();
            }
            // if data exists or offline
            return of(new HttpResponse({ status: 200, body: data }))
          } else {
            // if data not exists load it from backend and save it for next requests
            return handleRequestAndCacheResponse;
          }
        })
      );
  }



  private handleI18nRequest(request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    return from(this.offlineService.getData(request)).pipe(
      map(data => new HttpResponse({ status: 200, body: data }))
    );
  }

  private handleMaterialIconsRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return from(this.offlineService.getData(request)).pipe(
      switchMap(data => {
        if (data != null && data !== '') {
          return of(new HttpResponse({ status: 200, body: data }));
        }
      // if local copy missing we make real request
        return next.handle(request);
      }),
    );
  }

  private handleFaultAndAttachmentRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Handle Attachments
    if (this.isValidAttachmentRequest(request.url)) {
      const tilesPattern = /\/attachments\/\d+\/tiles/;
      if (tilesPattern.test(request.url)) {
        if(this.connectivityService.isOfflineVal || this.platformModeStateService.isLocalMode) {
          return from(this.offlineService.getData(request)).pipe(
            map(data => new HttpResponse({ status: 200, body: data }))
          );
        }

        if (this.isAttachmentTileRequestWithAccessToken(request.url)) {
          return next.handle(request);
        }

        return next.handle(request).pipe(
          tap(event => {
            if (event instanceof HttpResponse && event.status === 200) {
              this.offlineService.saveDataByUrl(request, event.body);
            }
          })
        );

      }
    }

    return next.handle(request);
  }

  private handleUserfieldsRequest(): Observable<HttpEvent<unknown>> {
    return of(new HttpResponse({ status: 200, body: [] }));
  }

  private handleDefaultRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.connectivityService.isOfflineVal) {
      return from(this.offlineService.getData(request)).pipe(
        map(data => new HttpResponse({ status: 200, body: data }))
      );
    }

    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse && event.status === 200) {
          this.offlineService.saveDataByUrl(request, event.body);
        }
      })
    );
  }

  public getCollectionFromUrl(url: string): MmProjectDbCollections | null {
    if(url.endsWith('user/permissions')){
      return null;
    }
    const endpoint = url.toLowerCase().split('/').pop();
    switch (endpoint) {
      case 'layouts':
        return MmProjectDbCollections.layouts;
      case 'crafts':
        return MmProjectDbCollections.crafts;
      case 'orders':
        return MmProjectDbCollections.orders;
      case 'contacts':
        return MmProjectDbCollections.contacts;
      case 'documenttypes':
        return MmProjectDbCollections.documentTypes;
      case 'companies':
        return MmProjectDbCollections.companies;
      case 'attributes':
        return MmProjectDbCollections.attributes;
      case 'attributegroups':
        return MmProjectDbCollections.attributeGroups;
      case 'fields':
        return MmProjectDbCollections.fields;
      case 'userdefineds':
        return MmProjectDbCollections.userDefineds;
      case 'settings':
        return MmProjectDbCollections.projectSettings;
      case 'permissions':
        return MmProjectDbCollections.userPermissions;
      case 'classifications':
        return MmProjectDbCollections.classifications;
      case 'delays':
        return MmProjectDbCollections.delays;
      case 'status':
        return MmProjectDbCollections.statuses;
      case 'plans':
        return MmProjectDbCollections.plans;
      case 'structures':
        return MmProjectDbCollections.structures;
      case 'pools':
        return MmProjectDbCollections.pools;
      default:
        return null;
    }
  }

  private isValidAttachmentRequest(url: string): boolean {
    return url.includes('/attachments') && !url.includes('/attachments/originals');
  }

  private isAttachmentTileRequestWithAccessToken(url: string): boolean {
    return /\/attachments\/\d+\/tiles\/\d+\/\d+\/\d+\?/.test(url) && url.includes('access_token');
  }

  private isFaultAndAttachmentRequest(url: string): boolean {
    return this.FAULT_ENDPOINTS.some(endpoint => url.includes(endpoint))
      || this.isValidAttachmentRequest(url);
  }

  private shouldTriggerOfflineMessage(url: string): boolean{
    return !this.excludedEndpointsForMessage.some(endpoint => url.includes(endpoint));
  }

  private shouldTriggerOfflineMessageWithCooldown(url: string): boolean{
    return this.endpointsForMessageWithCooldown.some(endpoint => url.includes(endpoint));
  }
}
